// 轮询检查新构建的入口资源是否已可用（200），避免刷新后404
export async function waitForNewBuildReady({
  versionUrl = '/version.json',
  maxWaitMs = 240_000, // 默认4分钟超时（适合部署流程约3分钟的场景）
  intervalMs = 3_000, // 默认每3秒检查一次
  maxAttempts = 80, // 最大重试次数（对应4分钟）
  onWaiting, // (attempt, remainingMs, lastError) => void
  signal, // AbortSignal 可选
  currentVersion, // 当前版本号，如果检测到版本已一致则直接返回
  deployWaitMs = 90_000, // 检测到新版本后先等待的时间（Deploy阶段约1-2分钟）
} = {}) {
  const started = Date.now()
  let attempt = 0
  let lastError = null
  let newVersionDetected = false
  let newVersionDetectedAt = null
  let consecutiveSuccessCount = 0 // 连续成功检查次数
  const REQUIRED_CONSECUTIVE_SUCCESS = 3 // 需要连续3次检查都成功

  while (true) {
    if (signal?.aborted) throw new Error('aborted')
    
    attempt += 1
    if (attempt > maxAttempts) {
      throw new Error(`Max attempts (${maxAttempts}) reached. Last error: ${lastError || 'unknown'}`)
    }

    try {
      // 强制绕过缓存
      const fetchOptions = { cache: 'no-store' }
      // 单次请求超时处理（兼容不支持AbortSignal.timeout的浏览器）
      let timeoutId = null
      if (!signal?.aborted) {
        const timeoutController = new AbortController()
        timeoutId = setTimeout(() => timeoutController.abort(), 5000)
        fetchOptions.signal = timeoutController.signal
      }
      
      const vRes = await fetch(`${versionUrl}?_t=${Date.now()}`, fetchOptions).finally(() => {
        if (timeoutId) clearTimeout(timeoutId)
      })
      if (!vRes.ok) {
        lastError = `version.json returned ${vRes.status}`
        throw new Error(lastError)
      }
      
      const v = await vRes.json()
      const entry = v?.entry
      
      // 如果提供了当前版本号且版本一致，说明已经在用新版本了
      if (currentVersion && v.version === currentVersion) {
        console.log('[Update] Version already matches, ready to reload')
        return { version: v.version, entry: entry || '/index.html' }
      }
      
      // 首次检测到新版本
      if (!newVersionDetected && currentVersion && v.version !== currentVersion) {
        newVersionDetected = true
        newVersionDetectedAt = Date.now()
        console.log(`[Update] New version detected: ${currentVersion} -> ${v.version}, waiting for deploy to complete...`)
      }
      
      // 如果已检测到新版本，需要等待 Deploy 阶段完成后再开始检查资源
      if (newVersionDetected) {
        const elapsedSinceDetection = Date.now() - newVersionDetectedAt
        if (elapsedSinceDetection < deployWaitMs) {
          const remainingWait = Math.ceil((deployWaitMs - elapsedSinceDetection) / 1000)
          lastError = `Waiting for deploy to complete (${remainingWait}s remaining)`
          console.log(`[Update] ${lastError}`)
          if (onWaiting) {
            const totalRemaining = maxWaitMs - (Date.now() - started)
            onWaiting(attempt, totalRemaining, lastError)
          }
          await new Promise(r => setTimeout(r, intervalMs))
          continue
        }
      }

      if (!entry) {
        // 如果没有entry字段，尝试直接检查index.html
        console.warn('[Update] entry missing in version.json, trying index.html')
        const indexOptions = { method: 'HEAD', cache: 'no-store' }
        let indexTimeoutId = null
        if (!signal?.aborted) {
          const indexTimeoutController = new AbortController()
          indexTimeoutId = setTimeout(() => indexTimeoutController.abort(), 5000)
          indexOptions.signal = indexTimeoutController.signal
        }
        
        const indexRes = await fetch('/index.html?_t=' + Date.now(), indexOptions).finally(() => {
          if (indexTimeoutId) clearTimeout(indexTimeoutId)
        })
        if (indexRes.ok) {
          return { version: v.version, entry: '/index.html' }
        }
        lastError = 'entry missing and index.html not ready'
        throw new Error(lastError)
      }

      const entryUrl = entry.startsWith('/') ? entry : `/${entry}`

      // 用HEAD检查，避免下载大文件
      let ok = false
      let entryStatus = 0
      try {
        const headOptions = { method: 'HEAD', cache: 'no-store' }
        let headTimeoutId = null
        if (!signal?.aborted) {
          const headTimeoutController = new AbortController()
          headTimeoutId = setTimeout(() => headTimeoutController.abort(), 5000)
          headOptions.signal = headTimeoutController.signal
        }
        
        const headRes = await fetch(`${entryUrl}?_t=${Date.now()}`, headOptions).finally(() => {
          if (headTimeoutId) clearTimeout(headTimeoutId)
        })
        ok = headRes.ok
        entryStatus = headRes.status
      } catch (headErr) {
        console.warn(`[Update] HEAD failed for ${entryUrl}:`, headErr.message)
      }
      
      // HEAD失败时尝试GET
      if (!ok) {
        try {
          const getOptions = { method: 'GET', cache: 'no-store' }
          let getTimeoutId = null
          if (!signal?.aborted) {
            const getTimeoutController = new AbortController()
            getTimeoutId = setTimeout(() => getTimeoutController.abort(), 5000)
            getOptions.signal = getTimeoutController.signal
          }
          
          const getRes = await fetch(`${entryUrl}?_t=${Date.now()}`, getOptions).finally(() => {
            if (getTimeoutId) clearTimeout(getTimeoutId)
          })
          ok = getRes.ok
          entryStatus = getRes.status
        } catch (getErr) {
          lastError = `Entry check failed: ${getErr.message}`
          console.warn(`[Update] GET failed for ${entryUrl}:`, getErr.message)
        }
      }
      
      if (!ok) {
        lastError = `Entry ${entryUrl} returned ${entryStatus || 'unknown status'}`
        consecutiveSuccessCount = 0 // 重置连续成功计数
      } else {
        // Entry 文件已就绪，现在检查 manifest.json 中的所有资源文件
        console.log(`[Update] Entry ${entryUrl} is ready (attempt ${attempt}), checking manifest resources...`)
        
        try {
          // 读取 manifest.json 检查所有资源文件
          const manifestOptions = { cache: 'no-store' }
          let manifestTimeoutId = null
          if (!signal?.aborted) {
            const manifestTimeoutController = new AbortController()
            manifestTimeoutId = setTimeout(() => manifestTimeoutController.abort(), 8000)
            manifestOptions.signal = manifestTimeoutController.signal
          }
          
          const manifestRes = await fetch(`/manifest.json?_t=${Date.now()}`, manifestOptions).finally(() => {
            if (manifestTimeoutId) clearTimeout(manifestTimeoutId)
          })
          
          if (manifestRes.ok) {
            const manifest = await manifestRes.json()
            // 提取所有资源文件路径（JS/CSS等）
            const resourceFiles = []
            for (const key in manifest) {
              const file = manifest[key]?.file
              if (file && (file.endsWith('.js') || file.endsWith('.css'))) {
                const normalized = file.startsWith('/') ? file : `/${file}`
                resourceFiles.push(normalized)
              }
            }
            
            // 检查关键资源文件是否就绪（最多检查前8个，覆盖主要资源）
            const resourcesToCheck = resourceFiles.slice(0, 8)
            console.log(`[Update] Checking ${resourcesToCheck.length} resources from manifest:`, resourcesToCheck.map(r => r.split('/').pop()))
            
            const resourceChecks = await Promise.allSettled(
              resourcesToCheck.map(async (resource) => {
                try {
                  const resOptions = { method: 'HEAD', cache: 'no-store' }
                  let resTimeoutId = null
                  const resTimeoutController = new AbortController()
                  resTimeoutId = setTimeout(() => resTimeoutController.abort(), 3000)
                  resOptions.signal = resTimeoutController.signal
                  
                  const resRes = await fetch(`${resource}?_t=${Date.now()}`, resOptions).finally(() => {
                    if (resTimeoutId) clearTimeout(resTimeoutId)
                  })
                  return { resource, ok: resRes.ok, status: resRes.status }
                } catch (e) {
                  return { resource, ok: false, error: e.message }
                }
              })
            )
            
            const failedResources = resourceChecks.filter(
              r => r.status === 'fulfilled' && !r.value.ok
            ).map(r => r.value.resource.split('/').pop())
            
            if (failedResources.length > 0) {
              lastError = `Resources not ready: ${failedResources.join(', ')}`
              console.warn(`[Update] Some resources not ready (${consecutiveSuccessCount}/${REQUIRED_CONSECUTIVE_SUCCESS}):`, failedResources)
              consecutiveSuccessCount = 0 // 重置连续成功计数
            } else {
              consecutiveSuccessCount += 1
              console.log(`[Update] All resources ready (${consecutiveSuccessCount}/${REQUIRED_CONSECUTIVE_SUCCESS} consecutive checks)`)
              
              // 需要连续多次检查都成功才认为真正就绪
              if (consecutiveSuccessCount >= REQUIRED_CONSECUTIVE_SUCCESS) {
                // 再等待3秒确保资源完全上传完成
                console.log(`[Update] Resources verified ${REQUIRED_CONSECUTIVE_SUCCESS} times, waiting 3s before reload...`)
                await new Promise(r => setTimeout(r, 3000))
                return { version: v.version, entry: entryUrl }
              }
            }
          } else {
            // manifest.json 读取失败，重置计数
            lastError = `Could not read manifest.json (${manifestRes.status})`
            console.warn(`[Update] ${lastError}`)
            consecutiveSuccessCount = 0
          }
        } catch (manifestErr) {
          // manifest 检查失败，重置计数
          lastError = `Failed to check manifest: ${manifestErr.message}`
          console.warn(`[Update] ${lastError}`)
          consecutiveSuccessCount = 0
        }
      }
    } catch (e) {
      lastError = e.message || String(e)
      console.warn(`[Update] Attempt ${attempt} failed:`, lastError)
    }

    const elapsed = Date.now() - started
    const remaining = maxWaitMs - elapsed
    if (remaining <= 0) {
      throw new Error(`Wait timeout after ${elapsed}ms. Last error: ${lastError || 'unknown'}`)
    }
    
    // 只在特定间隔提示（每3次尝试提示一次，避免过于频繁）
    if (onWaiting && (attempt % 3 === 0 || attempt === 1)) {
      onWaiting(attempt, remaining, lastError)
    }
    
    await new Promise(r => setTimeout(r, intervalMs))
  }
}
